// /scada/station/:id — station card (R2). Tabs: overview, single-line diagram,
// inverters/strings, КТП, BESS. Equipment detail is mock, consistent with the
// fleet overview numbers.
//
// Активна вкладка живе В АДРЕСІ (/scada/station/:id/:tab), не в useState:
// F5, Back/Forward і прямі посилання працюють для вкладок так само, як для
// розділів. Старий шлях без :tab лишається робочим і читається як «Огляд».
// Саме підменю малює ScadaLayout — тут його немає, конкуруючої навігації
// на сторінці більше не існує. У «above» лишається лише рядок ідентифікації.
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL, authFetch } from '@/services/api';
import { SectionCard, MetricTile } from '@/components/scada/kit';
import RadialGauge from '@/components/base/RadialGauge';
import { MetricStack } from '@/components/base/MetricPanel';
import BessHistory from '@/components/scada/BessHistory';
import { InverterMatrix } from '@/components/scada/stationViews';
import StationScheme from '@/components/scada/scheme/StationScheme';
import StationSld from '@/components/scada/scheme/sld/StationSld';
import { STATION_SLD } from '@/scada/stations';
import { getStoredUser } from '@/hooks/useAuth';
import { InverterStrip, StationDayChart } from '@/components/scada/InverterStrip';
import InverterPanel from '@/components/scada/InverterPanel';
import BessConsolidated from '@/components/scada/BessConsolidated';
import BatteryViz from '@/components/scada/BatteryViz';
import LuchkyInverterBoard from '@/components/scada/luchky/InverterBoard';
import LuchkyBessBoard from '@/components/scada/luchky/BessBoard';
import { BatteryCharging, BatteryLow, Pause, TriangleAlert, Cpu, Zap, Activity } from 'lucide-react';
import { scada } from '@/scada/tokens';
import { eicType } from '@/utils/eic';
import { STATIONS } from '@/scada/mock/stations';
import { useStationTelemetry } from '@/scada/useTelemetry';
import { buildStationDetail } from '@/scada/mock/stationDetail';
import CommercialMetersWidget from '@/components/scada/CommercialMetersWidget';

import { DEFAULT_STATION_TAB, isStationTab, type StationTab } from '@/scada/navConfig';

// Рядок ідентифікації станції («ТОВ ТЕПЛА / EIC») належить ScadaLayout —
// сторінка його НЕ малює. Інакше він існував би лише тут і зникав на
// «Ручному керуванні» та решті розділів, як і було до цієї правки.

// МВт з комою-роздільником (укр. локаль): 22352 → «22,35».
const mw = (kw: number) => (kw / 1000).toFixed(2).replace('.', i18n.language === 'en' ? '.' : ',');

export default function ScadaStationPage() {
  useTranslation();
  const { id, tab: tabParam } = useParams();
  const cfg = STATIONS.find((s) => s.id === id);
  const { data: t } = useStationTelemetry(id ?? '');
  // Невідома вкладка в адресі не ламає екран — показуємо огляд. «УЗЕ» більше
  // НЕ перекидає на огляд: вкладка відкривається завжди й сама пояснює, чому
  // даних немає. Інакше пряме посилання мовчки вело б не туди.
  const tab: StationTab = isStationTab(tabParam) ? tabParam : DEFAULT_STATION_TAB;
  const detail = useMemo(() => (cfg && t ? buildStationDetail(cfg, t) : null), [cfg, t]);

  // Розширена телеметрія УЗЕ (окремий легкий ендпоінт, агрегація по 16 PCS +
  // енергія за поточну годину). Тягнемо лише на вкладці УЗЕ, оновлюємо @20с.
  const assetId = t?.assetId;
  const hasBess = !!t?.bess;
  const [bessDetail, setBessDetail] = useState<any | null>(null);
  useEffect(() => {
    if (tab !== 'bess' || !assetId || !hasBess) { setBessDetail(null); return; }
    let alive = true;
    const load = () => authFetch(`${API_BASE_URL}/scada/bess/${assetId}/detail`)
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive) setBessDetail(d); }).catch(() => {});
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [tab, assetId, hasBess]);

  if (!cfg || !t || !detail) {
    return <SectionCard title={i18n.t('ak_scada_station_not_found')}><span style={{ color: scada.textMuted }}>{i18n.t('ak_scada_unknown_station_identifier')}</span></SectionCard>;
  }

  const gaugeColor = t.state === 'alarm' || t.state === 'offline' ? scada.state.alarm
    : t.limitReason ? scada.state.curtailed : scada.state.ok;


  return (
    <>
      {/* OVERVIEW */}
      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            <SectionCard fill title={i18n.t('ak_scada_station_power')} subtitle={i18n.t('ak_scada_dc_ac_mw', { dc: mw(cfg.dcCapacityKwp), ac: mw(cfg.acCapacityKw) })}>
              {/* Гейдж + метрики — стиль EMS Box («ФЕС · сонячна генерація»),
                  без тематичного малюнка: тут це службова SCADA-картка, а не
                  вітринний екран. Данi злiва пiд заголовком, гейдж справа -
                  тому justify-content:space-between замiсть center, i порядок
                  блокiв поміняно мiсцями. */}
              <div className="flex items-center justify-between" style={{ gap: 24, flexWrap: 'wrap', padding: 8 }}>
                {/* Кегль числа 23 і розкладка space-evenly без власного проміжку —
                    рівно як у колонці метрик «ФЕС · сонячна генерація» на /ems:
                    дві секції мають читатись як одна система, а не як дві схожі. */}
                <MetricStack
                  layout="column"
                  valueSize={23}
                  gap={0}
                  fill
                  items={[
                    {
                      Icon: Cpu, label: i18n.t('ak_scada_inverters_online'), value: `${t.invertersWorking}/${t.inverterCount}`,
                      color: t.invertersWorking < t.inverterCount ? scada.state.warning : scada.state.ok,
                    },
                    { Icon: Zap, label: i18n.t('ak_scada_generated_today'), value: (t.dailyEnergyKwh / 1000).toFixed(1), unit: i18n.t('ak_scada_mwh') },
                    // Було «Доступна» = t.availablePowerKw, а це рівно те саме
                    // число, що в гейджі (запас без прогнозу невідомий). Замість
                    // дубля — DC на вході інверторів, як у колонці EMS Box:
                    // разом із AC у гейджі показує кліпінг і втрати перетворення.
                    { Icon: Activity, label: i18n.t('ak_scada_dc_input'), value: t.dcPowerKw == null ? '—' : (t.dcPowerKw / 1000).toFixed(2), unit: t.dcPowerKw == null ? '' : i18n.t('ak_scada_mw') },
                  ]}
                />
                {/* valueScale менший за замовчуванням 0.23 - пятизначнi кВт
                    (напр. "14 711") заледве влазили пiд дугу впритул. */}
                <RadialGauge
                  value={t.activePowerKw} max={cfg.acCapacityKw} unit={i18n.t('ak_scada_kw')} color={gaugeColor} size={196} valueScale={0.185}
                  sub={i18n.t('ak_scada_of_capacity', { p: cfg.acCapacityKw ? Math.round((t.activePowerKw / cfg.acCapacityKw) * 100) : 0 })}
                />
              </div>
            </SectionCard>
            {/* 2. Комерційний облік (DLMS) — без BESS займає 2/3 ширини (без збільшення висоти) */}
            <div className={`flex flex-col ${t.bess ? '' : 'md:col-span-2'}`}>
              {['bolgrad', 'prometheus', 'tabaki', 'nikopol', 'luchky'].includes(cfg.id) ? (
                <CommercialMetersWidget endpoint={`/scada/${cfg.id}/meters`} cols={t.bess ? 3 : 4} />
              ) : (
                <SectionCard fill title={i18n.t('ak_scada_commercial_metering_dlms')} subtitle={i18n.t('ak_scada_direct_dlms_reading_ozd')}>
                  <div className="flex items-center justify-center" style={{ flex: 1, minHeight: 130 }}>
                    <span style={{ color: scada.textMuted, fontSize: 13, textAlign: 'center' }}>{i18n.t('ak_scada_commercial_metering_data_is_not_availa')}</span>
                  </div>
                </SectionCard>
              )}
            </div>

            {/* 3. УЗЕ / Накопичувач — лише за наявності BESS (інакше колонка не займає місце) */}
            {t.bess && (
              <div className="flex flex-col">
                <BessConsolidated fill bess={t.bess} capacityKwh={cfg.bessCapacityKwh} />
              </div>
            )}
          </div>

          {/* Row 2: технічний стан інверторів · Row 3: рівномірність (для всіх станцій) */}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <InverterStrip assetId={t.assetId} />
            <StationDayChart assetId={t.assetId} />
          </div>
        </>
      )}

      {/* SLD - без SectionCard: схема лежить прямо на фоні сторінки, а не в
          обмежуючій панелі. Заголовок лишається простим текстом (та сама
          типографіка, що в шапці SectionCard), картки вузлів мають власний
          фон і вже читаються самі по собі. */}
      {/* Лучки для обраних акаунтів (LUCHKY_SCHEME_LOGINS): схема на реальних
          даних і адресній відомості з ТЗ EMS box — привʼязка інверторів до
          логгерів і розвʼязка КТП на секції D1/D2. Решта бачить попередню. */}
      {/* 21.10: схема з гілки /tg (StationSld + конфіг src/scada/stations) — для
          ВСІХ акаунтів стенду на станціях, що мають конфіг (зараз Лучки). У /tg
          вона ще під гейтом canSeeStationSld (volodp); попередня LuchkyScheme
          лишилась у репозиторії, але тут більше не підключена. */}
      {tab === 'sld' && (STATION_SLD[cfg.id] && t.assetId ? (
        <div>
          <div style={{ padding: '10px 14px' }}>
            <div style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{i18n.t('ak_scada_station_single_line_diagram')}</div>
          </div>
          <StationSld assetId={t.assetId} topology={STATION_SLD[cfg.id].topology}
            drawing={STATION_SLD[cfg.id].drawing} limitKw={t.activeLimitKw} />
        </div>
      ) : (
        <div>
          <div style={{ padding: '10px 14px' }}>
            <div style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{i18n.t('ak_scada_station_single_line_diagram')}</div>
            <div style={{ color: scada.textMuted, fontSize: 11 }}>{i18n.t('ak_scada_point_of_connection_ktp_inverter_group')}</div>
          </div>
          <StationScheme t={t} detail={detail} />
        </div>
      ))}

      {/* INVERTERS — попередня повнофункцiональна панель (InverterPanel):
          клiк на iнвертор вiдкриває деталi по струму/напрузi за добу. Цю
          функцiональнiсть загубили, коли тимчасово пiдмiнили вкладку на
          InverterStrip (той самий вигляд, що на «Огляд») - повернуто назад. */}
      {/* Лучки: інвертори за КТП → секція D1/D2 під позначеннями зі схеми
          станції (ТЗ EMS box, дод. Б) — запит диспетчерів 16.10. Решта СЕС
          адресної відомості ще не має й лишається на InverterPanel. */}
      {tab === 'inverters' && (
        cfg.id === 'luchky' && t.assetId
          ? <LuchkyInverterBoard assetId={t.assetId} />
          : t.assetId
          ? <InverterPanel assetId={t.assetId} />
          : (
            <SectionCard title={i18n.t('ak_scada_inverters_running', { w: t.invertersWorking, n: t.inverterCount })} subtitle={i18n.t('ak_scada_click_an_inverter_to_view_its_strings')}>
              <InverterMatrix detail={detail} />
            </SectionCard>
          )
      )}

      {/* BESS / УЗЕ */}
      {/* Живих показань немає — показуємо причину, а не порожнечу. Чи є
          накопичувач узагалі, каже t.hasBess із бекенду (asset_bess_systems),
          а не статичний довідник у mock/stations.ts. */}
      {tab === 'bess' && !t.bess && (
        <SectionCard title={i18n.t('ak_scada_bess')} subtitle={t.hasBess ? i18n.t('ak_scada_storage_is_listed_in_the_asset_registr') : undefined}>
          <span style={{ color: scada.textMuted, fontSize: 13 }}>
            {t.hasBess
              ? i18n.t('ak_scada_storage_data_is_currently_not_coming_f')
              : i18n.t('ak_scada_this_solar_plant_has_no_energy_storage')}
          </span>
        </SectionCard>
      )}
      {tab === 'bess' && t.bess && (() => {
        const b = t.bess;
        const modeColor = b.mode === 'charging' ? scada.state.curtailed : b.mode === 'discharging' ? scada.state.ok : b.mode === 'fault' ? scada.state.alarm : scada.textDim;
        const n = (v?: number | null, d = 1) => (v == null ? '—' : Number(v).toFixed(d));
        const bd = bessDetail;
        const mwh = (v?: number | null) => (v == null ? '—' : (Number(v) / 1000).toFixed(1));
        const pcsLabel = (s?: string | null) => {
          if (!s) return '—';
          const m: Record<string, string> = {
            standby: i18n.t('ak_scada_standby'), charging: i18n.t('ak_scada_charge'), discharging: i18n.t('ak_scada_discharge'), normal: i18n.t('ak_scada_normal'),
            idle: i18n.t('ak_scada_idle'), fault: i18n.t('ak_scada_alarm'), running: i18n.t('ak_scada_running'), offline: i18n.t('ak_scada_offline'), no_charging: i18n.t('ak_scada_not_charging'),
          };
          return m[s] ?? s;
        };
        return (
          <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* SOC — батарея по центру (як на /ems), статуси ліворуч, числа праворуч */}
            <SectionCard fill title={i18n.t('ak_scada_state_of_charge_soc')} subtitle={i18n.t('ak_scada_storage_kehua_pcs_jinko_battery')}>
              <div style={{ display: 'flex', flex: 1, minHeight: 220, gap: 8 }}>
                {/* Секція 1 — статуси */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { label: i18n.t('ak_scada_charge'), Icon: BatteryCharging, color: scada.state.curtailed, active: b.mode === 'charging' },
                      { label: i18n.t('ak_scada_discharge'), Icon: BatteryLow, color: scada.state.ok, active: b.mode === 'discharging' },
                      { label: i18n.t('ak_scada_standby'), Icon: Pause, color: scada.textMuted, active: b.mode === 'idle' },
                      { label: i18n.t('ak_scada_alarm'), Icon: TriangleAlert, color: scada.state.alarm, active: b.mode === 'fault' },
                    ].map((st) => (
                      <div key={st.label} style={{ display: 'flex', alignItems: 'center', gap: 9, opacity: st.active ? 1 : 0.32, transition: 'opacity .4s' }}>
                        <span style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: st.active ? `color-mix(in srgb, ${st.color} 18%, transparent)` : scada.panel2, boxShadow: st.active ? `0 0 13px color-mix(in srgb, ${st.color} 55%, transparent)` : 'none', transition: 'background .4s, box-shadow .4s' }}>
                          <st.Icon size={17} strokeWidth={2} style={{ color: st.active ? st.color : scada.textDim }} />
                        </span>
                        <span style={{ fontSize: 12, color: st.active ? scada.text : scada.textDim, transition: 'color .4s' }}>{st.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Секція 2 — батарейка */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BatteryViz soc={b.socPct} charging={b.mode === 'charging'} fault={b.mode === 'fault'} h={220} />
                </div>
                {/* Секція 3 — числа: SoC + потужність + SOH */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, justifyContent: 'center' }}>
                      <span style={{ fontSize: 44, fontWeight: 700, lineHeight: 1, color: scada.text }}>{n(b.socPct, 0)}</span>
                      <span style={{ fontSize: 18, color: scada.textDim }}>%</span>
                    </div>
                    <div style={{ fontSize: 11, color: scada.textDim, marginTop: 5, letterSpacing: '.05em' }}>{i18n.t('ak_scada_soc_level')}</div>
                  </div>
                  {/* Поточна потужність потоку. Напрям показує КОЛІР (modeColor),
                      тож слово-статус не дублюємо — воно вже у лівому стовпчику. */}
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 17, fontWeight: 700, color: modeColor, lineHeight: 1 }}>{Math.abs(b.powerKw).toFixed(0)}<span style={{ fontSize: 11, color: scada.textDim, fontWeight: 400 }}> {i18n.t('ak_scada_kw')}</span></span>
                    <div style={{ fontSize: 11, color: scada.textDim, marginTop: 4 }}>{i18n.t('ak_scada_power_now')}</div>
                  </div>
                  <span style={{ fontSize: 11, color: scada.textMuted }}>SOH: {n(b.sohPct, 0)}%</span>
                </div>
              </div>
            </SectionCard>

            {/* Телеметрія УЗЕ — 4 компактні стовпці. Дані з /scada/bess/:id/detail
                (агрегація по 16 PCS + енергія за годину); fallback на стрім (b). */}
            <div className="md:col-span-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                {/* Потік зараз + енергія за годину (ключове) */}
                <MetricTile label={i18n.t('ak_scada_ac_power')} value={n(bd?.powerAcKw ?? b.powerKw, 1)} unit={i18n.t('ak_scada_kw')} accent={modeColor} quality={b.qualityFlag === 'ok' ? 'good' : 'stale'} />
                <MetricTile label={bd?.hourLabel ? i18n.t('ak_scada_charge_for_the_hour_from', { h: bd.hourLabel }) : i18n.t('ak_scada_charge_for_the_hour')} value={n(bd?.hourChargeKwh, 0)} unit={i18n.t('ak_scada_kwh')} accent={scada.state.curtailed} />
                <MetricTile label={bd?.hourLabel ? i18n.t('ak_scada_discharge_for_the_hour_from', { h: bd.hourLabel }) : i18n.t('ak_scada_discharge_for_the_hour')} value={n(bd?.hourDischargeKwh, 0)} unit={i18n.t('ak_scada_kwh')} accent={scada.state.ok} />
                <MetricTile label={i18n.t('ak_scada_available_power')} value={n(bd?.availablePowerKva, 0)} unit={i18n.t('ak_scada_kva')} />

                {/* Електрика */}
                <MetricTile label={i18n.t('ak_scada_dc_power')} value={n(bd?.powerDcKw ?? b.dcPowerKw, 1)} unit={i18n.t('ak_scada_kw')} />
                <MetricTile label={i18n.t('ak_scada_battery_voltage')} value={n(bd?.batteryV ?? b.batteryV, 1)} unit={i18n.t('ak_scada_v')} />
                <MetricTile label={i18n.t('ak_scada_battery_current')} value={n(bd?.batteryA ?? b.batteryA, 1)} unit={i18n.t('ak_scada_a')} />
                <MetricTile label={i18n.t('ak_scada_frequency')} value={n(bd?.gridHz ?? b.gridHz, 2)} unit={i18n.t('ak_scada_hz')} />

                {/* DC + термо */}
                <MetricTile label={i18n.t('ak_scada_dc_voltage')} value={n(bd?.dcV, 1)} unit={i18n.t('ak_scada_v')} />
                <MetricTile label={i18n.t('ak_scada_dc_current')} value={n(bd?.dcA, 1)} unit={i18n.t('ak_scada_a')} />
                <MetricTile label={i18n.t('ak_scada_temperature')} value={n(bd?.innerTempC ?? b.innerTempC, 0)} unit="°C" />
                <MetricTile label={i18n.t('ak_scada_pcs_heatsink')} value={n(bd?.radiatorTempC, 0)} unit="°C" accent={(bd?.radiatorTempC ?? 0) >= 60 ? scada.state.warning : undefined} />

                {/* Енергія: доба + всього */}
                <MetricTile label={i18n.t('ak_scada_charge_for_the_day')} value={n(bd?.dayChargeKwh ?? b.dailyChargeKwh, 0)} unit={i18n.t('ak_scada_kwh')} accent={scada.state.curtailed} />
                <MetricTile label={i18n.t('ak_scada_discharge_for_the_day')} value={n(bd?.dayDischargeKwh ?? b.dailyDischargeKwh, 0)} unit={i18n.t('ak_scada_kwh')} accent={scada.state.ok} />
                <MetricTile label={i18n.t('ak_scada_total_charged')} value={mwh(bd?.totalChargeKwh)} unit={i18n.t('ak_scada_mwh')} />
                <MetricTile label={i18n.t('ak_scada_total_discharged')} value={mwh(bd?.totalDischargeKwh)} unit={i18n.t('ak_scada_mwh')} />

                {/* Ємність + стани */}
                <MetricTile label={i18n.t('ak_scada_available_to_charge')} value={n(bd?.chargeAvailableKwh ?? b.chargeAvailableKwh, 0)} unit={i18n.t('ak_scada_kwh')} accent={scada.state.curtailed} />
                <MetricTile label={i18n.t('ak_scada_available_to_discharge')} value={n(bd?.dischargeAvailableKwh ?? b.dischargeAvailableKwh, 0)} unit={i18n.t('ak_scada_kwh')} accent={scada.state.ok} />
                <MetricTile label={i18n.t('ak_scada_pcs_status')} value={pcsLabel(bd?.runningStatus)} accent={bd?.anyFault ? scada.state.alarm : scada.state.ok} quality={bd?.anyFault ? 'read_error' : 'good'} />
                <MetricTile label={i18n.t('ak_scada_bms_status')} value={pcsLabel(bd?.bmsStatus)} accent={bd?.bmsStatus && bd.bmsStatus !== 'normal' ? scada.state.warning : scada.state.ok} />
              </div>
            </div>
          </div>
          {/* Лучки: блок УЗЕ → батарея → пара PCS A/B (ТЗ EMS box, § 2.2). */}
          {cfg.id === 'luchky' && t.assetId && (
            <div style={{ marginTop: 16 }}><LuchkyBessBoard assetId={t.assetId} /></div>
          )}
          <BessHistory assetId={t.assetId} />
          </>
        );
      })()}
    </>
  );
}
